import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';
import { MOTION_GRAPHICS_SCENE_DESIGN_ID } from 'js/src/agent/tools/motion-graphics/motionGraphicsSceneDesign';
import Flags from 'js/src/libs/flags';
import { recipeCopy } from 'js/src/agent/skills/recipeCopy';

export const createQuickMotionGraphicsVideoSkill: Skill = {
    name: SKILL_NAMES.createQuickMotionGraphicsVideo,
    description:
        'Quick motion graphics video — a voiced, fast-cut graphics video where one centred word, ' +
        'figure or icon is on screen at a time, each for about a second, handed to the next by a ' +
        'morph, match cut or transition landing on the word the narration is saying. Use for ' +
        'punchy explainers, kinetic-typography pieces, fast-paced social videos.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        ...recipeCopy(SKILL_NAMES.createQuickMotionGraphicsVideo),
        isAvailable: () => Flags.canUseCodeo(),
    },
    get instructions(): string {
        return `# Create Quick Motion Graphics Video

A motion graphics video cut to the voice: **one element on screen at a time** (one word, one
figure, or one drawn object), centred, held for about a second, then handed to the next by
motion. What makes it worth watching is that motion: words that turn into the things they
name, shapes that reshape into the next idea, cuts hidden inside a whip or an iris, every one
landing on the word the voice is saying. One element on screen does not mean one move. A
video that only fades words up and down, one after another, is this format failing.

Load the \`${SKILL_NAMES.createVideo}\` skill too. Everything there applies, and this skill's rules
win where they conflict. Every visual is a bespoke graphic: no footage, no photography, no
template designs. If the user's material is media-led (product shots, footage they want on
screen), say so plainly and offer \`${SKILL_NAMES.createVideo}\` instead.

## Structure
- **One AI voiceover scene group for the whole video**
  (\`${SKILL_NAMES.aiVoiceoverVideoBasics}\`), with every visual a graphic over it, the title and
  outro included. The format is always voiced, so set \`require_voiceover: true\` on the voice
  question rather than offering "No voiceover".
- Add the graphics with \`${TOOL_NAMES.addVoiceoverScene}\`, \`scene_design_id\`
  \`"${MOTION_GRAPHICS_SCENE_DESIGN_ID}"\`, in ONE call listing every word range in order. **Every
  transcript word is covered** (\`${SKILL_NAMES.voiceoverVideoBasics}\` has the coverage rules); an
  uncovered span shows raw waveform. Leave the base scene underneath alone.
- **Captions are off**: call ${TOOL_NAMES.setCaptions} with enabled: false right after generating
  the voiceover. The beats do that job.

## Script, written for beats
The visuals can only be as quick as the script gives them things to show.
- **Short sentences, concrete words.** Nouns that name a thing, numbers, verbs of change,
  a verdict. A sentence of abstractions ("leverage synergies across workflows") has no
  word worth putting on screen, so the graphic stalls or invents copy.
- **Something showable every two or three spoken words.** That is the cadence the beats
  run at. One claim per sentence.
- **Tone defaults to punchy and energetic** unless the user said otherwise.
- **No pauses at the seams.** A held graphic over silence is dead air; to reach a length,
  write more script.

## Graphics, then beats
**A graphic covers one idea, about 10 seconds**, so a 60-second video is about six of
them. Cut at the seams in the argument, not to the clock, and let them come out uneven:
4 seconds for the opening title, 14 for a comparison. Both sides of a comparison, a cause and
its effect, a problem and its fix belong in one graphic. The opening line is its own short
title graphic, and the close is a real outro graphic.

Inside a graphic, the idea unfolds as a run of **beats**, each one element.

- **A beat is one element, landing on its word.** Read the scene's cues and pick the words a
  listener would underline. Each becomes a beat at that word's cue. Never pick a function
  word, and never a whole phrase: if it takes longer to read than to say, it is too long.
- **Pace: one beat every 0.6–1.3 seconds** of narration: 8–12 in a ten-second graphic,
  **never fewer than 6 per 10 seconds**. Three keywords stretched across a scene is a slow
  graphic with long holds, not this format. Let the voice's own rhythm set the spacing:
  evenly spaced beats are the tell that you cut to a clock rather than to the words.
- **Every beat brings a new element.** A beat is not the last element scaling up, shifting,
  tightening or changing colour: that is filler dressed as a beat, and it is how a graphic
  ends up with three elements in ten seconds.
- **Hold about a second, never much more.** From landing to starting to leave, an element
  stays up for ~1s. If the next beat word is further off than that, there is usually a
  word in between worth a beat: use it.
- **One key beat per graphic may hold longer, up to ~2s.** It is the payoff, the figure or
  verdict the graphic exists for, and it is usually the last beat. Never two long holds in a
  row: the format's energy is in the contrast between the quick beats and the one that
  breathes.

What a beat can be, and **at least a third of each graphic's beats are drawn objects,
figures or diagrams, not words** (the short title graphic excepted). A graphic of words
alone is a subtitle track:
- **Words**: the spoken word itself, 1–3 words at most ("Speed", "3 hours", "No code").
  Use the narration's own word, not a paraphrase, because a paraphrase has no moment to land
  on. A figure the voice says in words is set as a figure ("forty percent" → 40%) and lands
  on its first spoken word.
- **One drawn object**, for a concrete noun with a recognisable silhouette, built by
  \`${SKILL_NAMES.objectConstructionMethod}\`. It has to read in under a second, so a bold
  silhouette, not an illustration. Reuse the video's stroke weight so objects from different
  graphics look like one set.
- **A tiny diagram**: two or three shapes and a relation (A → B, a circle split in two).
  Only when it reads at a glance. Anything that needs labels is too big for a beat.

Never two elements at once. No caption under the icon, no word beside the figure, no header
left behind. If a beat seems to need a second element, it is two beats.

## Timing: land on the word
The viewer should see each element **arrive as they hear its word**: not ahead of it, which
spoils it, and not after, which reads as lag.

- **Arrivals land on the cue.** Start the incoming move 0.05–0.15s before its word's cue,
  on the *settle* curve from \`${SKILL_NAMES.easingCurves}\`, so the element is legible at the onset. A drop is the
  exception: it falls on an ease-in and its first contact lands on the cue.
- **Departures straddle the next cue.** The outgoing element starts leaving ~0.1–0.2s before
  the next cue and is gone by the time its successor is readable. Never an empty frame
  longer than a couple of frames, and never two readable elements for longer than ~0.15s.
- **Handoffs are short**: 0.2–0.45s for the ordinary beats, up to ~0.6s for a morph or a
  transition. A handoff longer than the hold it serves is the motion upstaging the content.
- **Open on motion.** The first beat is arriving from 0s, not sitting there.
- **Land the last beat at least ~1s before the cut** and let it hold to the end. That is
  usually the key beat, so pick the graphic's boundary such that the payoff word is said
  with a second of sentence left after it.
- **Position every tween on its word's cue**: named constants, or a table of cues a loop
  walks, read off the scene's cues. Never a fixed stagger or an even interval: it drifts off
  the voice within three beats. A morph straddles its cue so the new form is recognisable as
  the word is said:

\`\`\`js
const CUE_CLOUD = 2.34, CUE_SERVER = 3.12; // the beats' words, from the cues
const LAND = 0.1; // arrive just ahead of the word, never behind it
const AT = (cue) => Math.max(0, cue - LAND);
// "cloud": the icon punches in on its word
tl.fromTo('#icon', { scale: 0.5, opacity: 0, rotation: -12 }, { scale: 1, opacity: 1, rotation: 0, duration: 0.38, ease: 'expo.out' }, AT(CUE_CLOUD));
// "server": the cloud's outline becomes the server's, resolving on the word
tl.to('#icon-path', { morphSVG: '#server-shape', duration: 0.45, ease: 'power3.inOut' }, CUE_SERVER - 0.35);
\`\`\`

## Act out the line
The strongest move on a beat is one that shows what its words say: when the voice says
something rises, falls, joins, splits or overtakes, the element does it.
\`${SKILL_NAMES.actOutTheLine}\` (required reading, see Build order) has how: reading each line for
its action, giving verbs of different strength different moves, and performing a verb spoken
between two beats as the handoff between them, best of all a match cut or morph in which the
subject acting out the verb becomes the figure. Two rules from it govern this recipe:
- **The meaning wins over variety.** Two lines that both say something rises both rise; the
  rule against repeating a move is for moves chosen for no reason.
- **A relationship needs both parties.** For that beat the pair is the one element, and the
  next beat takes the frame back to one.

## Handoffs: motion between the beats
Every change of element is a move, never a pop, and **each move is chosen for the two beats it
joins**. There is no house move repeated beat after beat: beats that all rise in as the last
rises out read as one effect on a loop. In each graphic:
- **No move twice in a row, and none more than twice in the graphic.** Vary the kind (morph,
  match cut, transition), the axis and direction, and the ease.
- **At least one morph or match cut.** Put the bigger moves on the key beat, on a turn in the
  argument, and wherever two beats share a form or one becomes the other. Plan the beats to
  create those moments: choose a drawn object because the next word can morph out of it, put
  two round things back to back so they can match-cut.
- **Moves that differ from the graphic before.** The same moves in consecutive graphics read
  as one effect applied to the video.

The handoff vocabulary, by what the two beats share:
- **One becomes the other: morph.** The word turning into the object it names
  (\`${SKILL_NAMES.typeAsObject}\`); one silhouette reshaping into the next
  (\`${SKILL_NAMES.facetMorphShape}\`); the card, pill or badge around one element
  reshaping around the next (\`${SKILL_NAMES.elementMorphHandoff}\`, only where both sit in one); each word rolling into the exact slot of the last
  (\`${SKILL_NAMES.wordSwapInPlace}\`, where the slot is the whole frame and no other words
  stay); a whole graphic carried on one changing shape (\`${SKILL_NAMES.morphChainSpine}\`). Use
  when the narration says one thing turns into, causes, or replaces another.
- **Continuity across a cut: match cut.** Several things of one kind cycling through one slot
  (\`${SKILL_NAMES.iconSwapCycle}\`); two beats sharing a silhouette (\`${SKILL_NAMES.shapeMatchCut}\`);
  the outgoing element's speed carried into the incoming one (\`${SKILL_NAMES.velocityMatchedCut}\`); with \`${SKILL_NAMES.motionBlurStreak}\` on the fast part, and
  \`${SKILL_NAMES.weightedExit}\` for an exit with weight; a feed or a screen for app content
  (\`${SKILL_NAMES.feedScrollDemo}\`, \`${SKILL_NAMES.deviceScreenDive}\`).
- **A change of moment: transition.** The bigger moves, for a new point, a reversal or the
  payoff, and the only places the ground colour may change: \`${SKILL_NAMES.irisReveal}\`,
  \`${SKILL_NAMES.zoomThroughTransition}\`, \`${SKILL_NAMES.slatWipeReveal}\`,
  \`${SKILL_NAMES.halftoneDotDissolve}\`, \`${SKILL_NAMES.lightSweepPass}\`,
  \`${SKILL_NAMES.momentSweepTransition}\`, \`${SKILL_NAMES.whipPanCut}\`, \`${SKILL_NAMES.pullBackReveal}\`,
  \`${SKILL_NAMES.panBetweenStations}\` (one element per station), and \`${SKILL_NAMES.sharedAxisSlide}\`
  (a quieter move for a sequence of steps).

Find the reason in the content: a morph between two unrelated words is noise. But a
graphic whose beats all change the same way is the more common failure, and it is never the
restrained choice. It is words sliding up and down.

## Physical motion: how each element moves
The handoffs say how one beat gives way to the next. The animation principles say how the
element itself moves while it arrives, holds and leaves: like an object with weight, not a
layer sliding on a rail. They are what make a one-element frame worth watching for a second.
- **Every move**: \`${SKILL_NAMES.easingCurves}\`. Arrivals on its *settle* curve, a move between two
  resting places on *easy ease*, a departure from rest on *gather*. Linear only for the x of an
  arc. Never linear on an arrival or an exit.
- **A drawn object that drops or lands**: \`${SKILL_NAMES.massAndWeight}\`. Choose the weight from
  the noun (a safe or a verdict lands heavy, a box medium, a balloon light). Heavy and medium
  are still inside a beat; light only on the beat that may hold longer.
- **Anything that travels through the air**, tossed in, hopping in, thrown out, goes on arcs:
  \`${SKILL_NAMES.arcPathMotion}\`.
- **A soft or round object landing, bouncing or launching** squashes and stretches:
  \`${SKILL_NAMES.squashAndStretch}\`. Rigid objects never do.
- **An element at rest that leaves with intent** winds up first:
  \`${SKILL_NAMES.anticipationWindUp}\`, with the wind-up and hold at the end of its hold.
- **An element that stops hard after a slide** lets its loose part carry on:
  \`${SKILL_NAMES.followThroughOverlap}\` (a stem, an antenna, or a word leaning on its baseline).
- **An element with a small attached part** (the dot of an i, a lid, a cap, a leaf) has that
  part thrown by its moves: \`${SKILL_NAMES.secondaryAction}\`.
- **The key beat** pushes its poses further than every other beat: \`${SKILL_NAMES.exaggeratedPoses}\`.

**Use one on every beat whose element can do it**, not only the key beat: a drop, a toss, a
wind-up and a thrown part can all sit in one graphic. They act inside one element: the part
that hops is part of the element, and nothing becomes a second element on screen.
- **Assignment: every graphic is assigned at least one of these principles**, on a beat whose
  element can do it (a part to throw, a body to land, a rest to leave from). A graphic of
  words and figures alone can still wind up and exaggerate its key beat. A graphic assigned
  no principle is the one that comes out as layers sliding on rails.
- **The rest are yours**: give every other beat that suits a principle its own, name it on
  the phase it acts on, load its skill and build it to that skill's numbers, like an assigned
  one. A drop with a bounce you made up, or a sway that comes from no skill, does not count
  as a principle.

On the beat clock, **the moment of arrival is the cue**: the first contact of a drop, the stop
of a slide. The bounces, the wobble and the hops that follow play out during the hold, which
is what keeps a held element alive. The next handoff may take the element before it is
completely still, once it has read (the first rebound, the first swing). These skills are
already written at this pace: keep their numbers, timings and curves as they are.

**The other techniques were written for slower graphics. Take their mechanics and replace their
clock with this one.** Keep the markup, the move, the eases and the pitfalls (transforms,
clipping, origins). Drop their dwell and hold numbers, along with "one per graphic",
"three states at most", "give each state real time", "keep something on screen across the
swap" and a caption set beside the object. The beat clock and the one-element rule win.

## Build order
1. \`${TOOL_NAMES.loadSkill}\` \`${SKILL_NAMES.motionGraphicsScenesBasics}\`, then
   \`${SKILL_NAMES.htmlComposition}\`, then \`${SKILL_NAMES.easingCurves}\`, then
   \`${SKILL_NAMES.actOutTheLine}\`, **before anything else**, the script included. They carry the
   build loop, the composition contract, the technique index, the curves every move is built
   on, and how the moves show what the narration says; lint rejects a composition written to
   rules you never read.
2. One \`${TOOL_NAMES.addVoiceoverScene}\` call with every word range.
3. \`${TOOL_NAMES.writeDesignSystem}\` **once, before the first plan**, so every graphic shares
   one type scale, stroke weight and ground (see Styling).
4. Then the graphics **one at a time, in play order**: plan, load techniques, author, then
   **\`${TOOL_NAMES.reviewSceneImages}\` on that graphic before you plan the next one**. Not in
   a batch at the end: a mistake caught in the first graphic is one you would otherwise
   repeat in all of them. Check two things the reviewer can't judge for you. **Sync**: each
   frame is labelled with what is being said, and its element should be the beat whose word
   is being said or was just said. **Moves**: follow up with \`seconds\` set just after the cue
   of each morph, match cut and transition, and check the frame shows the morph or cut you planned, not a
   fade.

Fill each \`${TOOL_NAMES.writePlan}\` as a beat sheet:
- \`text\`: every word beat, exactly as shown. \`graphics\`: every drawn object.
- \`layout\`: "one element at a time, centred".
- \`phases\`: **one per beat, and every phase has an \`enters\`**, with \`at\` on its word's cue
  (the first at 0) and \`beat\` the spoken word. \`exits\` is the element before it, or
  \`transforms\` says how the element before it *becomes* this one. **Every phase after the
  first names its move and the technique behind it** ("velocity-matched push up", "the word
  'cloud' morphs into a cloud icon: type as object"). A plan rejected for a phase that "only
  brings something new" needs that phase's \`exits\`, never an extra filler phase.
  Name the physical motion in the same words ("drops in heavy and rocks to a stop: mass and
  weight", "the lid hops as it lands: secondary action").

\`${TOOL_NAMES.writePlan}\` assigns techniques with this recipe in view, so its picks come from
the vocabulary above: build them on the beats it places them, and plan your other
moves around them. Then, in one turn, **load every technique the plan
names**: every handoff, the animation principles its beats use,
\`${SKILL_NAMES.objectConstructionMethod}\` when the graphic draws anything, and everything
\`${TOOL_NAMES.writePlan}\` assigned. Build each one
as its skill implements it (a morph is \`morphSVG\`, an iris is a growing \`clipPath\`, a whip
is a blur peaking mid-move), not as a fade or a push standing in for it. A graphic authored
with no technique loaded comes out as words sliding up and down, whatever its plan said.

An assigned technique from outside the three families (a list reveal, a counter, a chart
build) is built as one beat's own move, never as a second element on screen. From \`${SKILL_NAMES.sustainedSceneReveal}\`,
take the cue timing; its advice on overlapping and staggering several elements, and on
composing a resting frame, does not apply when the frame holds one thing.

## Styling
- **Centred on the frame**, on a full-frame grid (\`place-items: center\`) with
  \`line-height: 1\`, so the glyphs rather than the line box sit on the centre. The centre is
  the stage, and it stays put.
- **Type scale for beats.** \`--ds-type-hero\` is the one-word size and \`--ds-type-headline\` the
  2–3 word size. Size the hero so the longest word in the script fits inside the margins of
  this aspect ratio. Contrast between beats comes from scaling the element in the motion, not
  from a new type size.
- **One heavy weight, one family.** The key beat takes the accent (as type if it clears
  contrast, otherwise as a shape behind it); the rest are ink on ground.
- **Ground**: one per graphic, from the design system. It changes only through a transition
  handoff or at a scene cut, never as a hard swap mid-graphic.
- **Music**: a track with a steady, upbeat pulse, kept under the voice. The beats sync to
  the words, not the music.

## Where this overrides the standard build
These win over \`${SKILL_NAMES.createVideo}\`, over \`${SKILL_NAMES.htmlComposition}\`'s style
guidance, and over your standing build instructions.
- **Pacing**: ignore the ~3-4 second cap on anything staying on screen and "a full sentence
  needs two or more overlays". The graphic is the scene; the beats inside it are the pace.
- **Media, designs, charts**: no stock or user media search, no reframing, no template
  designs or design rotation, no chart asset. A figure worth seeing is a beat.
- **Phases are beats.** Ignore "one phase per five seconds" and "a title has room for one
  phase". A ten-second graphic has eight to twelve.
- **Centred is the format.** \`${SKILL_NAMES.htmlComposition}\` calls everything centred on a
  flat fill the biggest "generated" tell. Here, what keeps it from looking generated is the
  scale contrast between beats, the handoffs, and the timing.
- **Continuity is the handoff, never a carried element.** Nothing stays on screen as a
  header or anchor across beats.
- **On-screen text is the keyword, 1–3 words**, not a 2–8 word line.
- **Lint warnings that are this format working**: \`text-reading-time\` on a beat word that is
  up ~0.5s or longer while it is being spoken is expected, so leave it. Under ~0.5s it really
  is a flash, so hold it or drop the beat. \`text-overlap\` during a handoff: keep the overlap
  under ~0.15s, or mark a deliberate morph crossfade \`data-l5-allow-overlap\`.
- **Lint warnings that are real here**: \`text-out-of-time-with-narration\` always: retime to
  the cue. \`motion-dead-stretch\` means a beat is overstaying. \`motion-no-settle\` means the
  last beat landed too late.
- **Review findings**: a \`dead-element\` calling a phrase a restatement of the narration is
  right, so cut it to its keyword. A single spoken keyword on screen is this format.

## Review
Load \`${SKILL_NAMES.reviewVideo}\`. You have reviewed each graphic as you built it, so this pass
is about the video as a whole:
- **Coverage**: every transcript word is covered, the title and outro are graphics, and
  captions are off.
- **Sync**: in the whole-video review frames, each element is the beat whose word is being
  said or was just said.
- **One element per frame**: no frame holds two readable elements outside a handoff.
- **Beats**: read them back from the plans. At least 6 per 10 seconds, every one a new
  element, at least a third of them drawn objects, figures or diagrams. No gap between
  consecutive beats runs past ~1.3s except the one key beat (≤ ~2s), and every graphic's
  last beat lands a second before its cut.
- **Moves**: every beat change is a move; no move runs twice in a row or more than twice in
  a graphic; every graphic has a morph or match cut; and no graphic repeats the moves of the
  graphic before.
- **Acted out**: every beat whose words say something happens has a move that shows it,
  landing on the verb, and a verb spoken between two beats is performed by the handoff
  between them.
- **Physical motion**: every beat whose element can take an animation principle has one,
  each named in its plan and built to its skill's numbers. A
  part that only appears after its body lands is a reveal, not a secondary action. Look at a landing with \`seconds\` set on its first contact:
  the contact lands on its word, a squashed shape sits exactly on its floor, and nothing
  linear arrives or leaves.
- **Copy**: every word beat is the narration's own keyword, 1–3 words, centred, and the
  \`texts\` in the video state carry exactly the words and figures you planned.`;
    },
};
